/* eslint-disable max-lines */
import { Button } from '../../../../../components/Clickables/Buttons/Button';
import { InternalLink } from '../../../../../components/Clickables/InternalLink/InternalLink';
import { FishingReport } from '../../../../../components/FishingReportsGrid/components/FishingReportCard';
import { PlaceLinks } from '../../../../../components/PlaceLinks/PlaceLinks';
import { getEdgesFromConnection } from '@graphql/connections/getEdgesFromConnection';
import { firstNonEmpty } from '@lib/strings/firstNonEmpty';
import { getFishingWaterUrl, getSpeciesUrl, getUserUrl } from '@domain/routes/urls';
import {
  FishingWaterCountryFields,
  FishingWaterRegionFields,
  PlaceTopSpeciesConnectionFields,
  PlaceTopSpeciesNodeFields,
  PlaceTopWaterNodeFields,
} from '../../../../../generated/graphql';

/** Edge shape from PlaceTopSpeciesConnectionFields. */
interface PlaceTopSpeciesEdge {
  catchesCount: number;
  node: PlaceTopSpeciesNodeFields | null;
}
import { convertCount } from '@lib/numbers/convertCount';
import { IFAQData } from '../../../../../components/FAQ/FAQ';
import { getPlaceLinksString } from '../../../../../components/PlaceLinks/getPlaceLinksString';
import ReactAnd from 'react-and';

interface IProps {
  country: FishingWaterCountryFields | null;
  placeName: string;
  placeSlug: string;
  handleCTAClick: () => void;
  region: FishingWaterRegionFields | null;
  recentCatchPostList: FishingReport[];
  topWaters: PlaceTopWaterNodeFields[];
  topSpeciesSummary?: PlaceTopSpeciesConnectionFields;
}

const NUMBER_OF_FISHING_REPORTS_TO_SHOW = 5;
const NUMBER_OF_FISHING_WATERS_TO_SHOW = 5;
const NUMBER_OF_SPECIES_TO_SHOW = 5;

export const getFAQData = ({
  country,
  placeName,
  placeSlug,
  handleCTAClick,
  region,
  recentCatchPostList,
  topWaters,
  topSpeciesSummary,
}: IProps): IFAQData[] => {
  const countryName = country?.countryName;
  const countryId = country?.id;
  const regionDisplayName = firstNonEmpty(region?.localName, region?.name);
  const speciesEdges = getEdgesFromConnection<PlaceTopSpeciesNodeFields, PlaceTopSpeciesEdge>(
    topSpeciesSummary,
  );

  return [
    ...(topWaters.length > 0
      ? [
          {
            id: '1',
            title: `🎣 Where to fish in ${placeName}, ${regionDisplayName}?`,
            body: (
              <>
                {`Anglers in `}
                <PlaceLinks
                  regionId={region?.id}
                  regionName={region?.name}
                  countryId={countryId}
                  countryName={countryName}
                  className="m-0 text-left"
                  linkClassName="body-text-sm"
                  textAs="span"
                  textVariant="md"
                  placeName={placeName}
                  placeSlug={placeSlug}
                />
                {` recommend these top fishing spots: `}
                <ReactAnd>
                  {topWaters
                    .slice(0, NUMBER_OF_FISHING_WATERS_TO_SHOW)
                    .map(({ _id, displayName }) => {
                      return (
                        <InternalLink
                          key={`topwater-${_id}`}
                          to={getFishingWaterUrl(_id, displayName)}
                          className="body-text-sm text-primary"
                        >
                          {displayName}
                        </InternalLink>
                      );
                    })}
                </ReactAnd>
                {'.'}
              </>
            ),
            bodyString: `Anglers in ${getPlaceLinksString({ countryId, countryName, placeName, placeSlug, regionId: region?.id, regionName: region?.name })} recommend these top fishing spots: ${topWaters
              .slice(0, NUMBER_OF_FISHING_WATERS_TO_SHOW)
              .map(
                ({ _id, displayName }) =>
                  `<a href="${getFishingWaterUrl(_id, displayName)}">${displayName}</a>`,
              )
              .join(', ')}.`,
          },
        ]
      : []),
    ...(speciesEdges.length > 0
      ? [
          {
            id: '2',
            title: `🐟 What fish can you catch in ${placeName}?`,
            body: (
              <>
                {`Most commonly caught species around `}
                <PlaceLinks
                  regionId={region?.id}
                  regionName={region?.name}
                  countryId={countryId}
                  countryName={countryName}
                  className="m-0 text-left"
                  linkClassName="body-text-sm"
                  textAs="span"
                  textVariant="md"
                  placeName={placeName}
                  placeSlug={placeSlug}
                />
                {`:`}
                <ul className="m-0 list-disc px-4 py-2">
                  {speciesEdges
                    .slice(0, NUMBER_OF_SPECIES_TO_SHOW)
                    .map(({ node, catchesCount }) => {
                      return (
                        <li className="body-text-sm m-0" key={`species-${node._id}`}>
                          <InternalLink
                            to={getSpeciesUrl(node._id, node.displayName)}
                            className="body-text-sm text-primary"
                          >
                            {node.displayName}
                          </InternalLink>
                          {` - ${convertCount(catchesCount)} reported catches`}
                        </li>
                      );
                    })}
                </ul>
              </>
            ),
            bodyString: `Most commonly caught species around ${getPlaceLinksString({ countryId, countryName, placeName, placeSlug, regionId: region?.id, regionName: region?.name })}: <ul>${speciesEdges
              .slice(0, NUMBER_OF_SPECIES_TO_SHOW)
              .map(
                ({ node, catchesCount }) =>
                  `<li><a href="${getSpeciesUrl(node._id, node.displayName)}">${node.displayName}</a> - ${convertCount(
                    catchesCount,
                  )} reported catches</li>`,
              )
              .join('')}</ul>.`,
          },
        ]
      : []),
    ...(recentCatchPostList.length > 0
      ? [
          {
            id: '3',
            title: `📢 What are the latest ${placeName} fishing reports?`,
            body: (
              <>
                {`The latest `}
                <PlaceLinks
                  regionId={region?.id}
                  regionName={region?.name}
                  countryId={countryId}
                  countryName={countryName}
                  className="m-0 text-left"
                  linkClassName="body-text-sm"
                  textAs="span"
                  textVariant="md"
                  placeName={placeName}
                  placeSlug={placeSlug}
                />
                {` fishing reports are: `}
                <ul className="m-0 list-disc px-4 py-2">
                  {recentCatchPostList.slice(0, NUMBER_OF_FISHING_REPORTS_TO_SHOW).flatMap(post => {
                    const userNickname = post.user.nickname;
                    const speciesId = post.catch?.species?._id;
                    const speciesName = post.catch?.species?.displayName;
                    const fishingWaterName = post.catch?.fishingWater?.displayName;
                    const fishingWaterId = post.catch?.fishingWater?._id;
                    if (!userNickname || !speciesId || !speciesName) {
                      return [];
                    }
                    return (
                      <li className="body-text-sm m-0" key={`post-${post._id}`}>
                        <InternalLink
                          to={getUserUrl(userNickname)}
                          className="body-text-sm text-primary"
                        >
                          {`@${userNickname}`}
                        </InternalLink>
                        {` reported a nice `}
                        <InternalLink
                          to={getSpeciesUrl(speciesId, speciesName)}
                          className="body-text-sm text-primary"
                        >
                          {speciesName}
                        </InternalLink>
                        {fishingWaterId && fishingWaterName && (
                          <>
                            {` at `}
                            <InternalLink
                              to={getFishingWaterUrl(fishingWaterId, fishingWaterName)}
                              className="body-text-sm text-primary"
                            >
                              {fishingWaterName}
                            </InternalLink>
                          </>
                        )}
                        {`.`}
                      </li>
                    );
                  })}
                </ul>
              </>
            ),
            bodyString: `The latest  ${getPlaceLinksString({ countryId, countryName, placeName, placeSlug, regionId: region?.id, regionName: region?.name })} fishing reports are: <ul>${recentCatchPostList
              .slice(0, NUMBER_OF_FISHING_REPORTS_TO_SHOW)
              .flatMap(post => {
                const userNickname = post.user.nickname;
                const speciesId = post.catch?.species?._id;
                const speciesName = post.catch?.species?.displayName;
                const fishingWaterName = post.catch?.fishingWater?.displayName;
                const fishingWaterId = post.catch?.fishingWater?._id;
                if (!userNickname || !speciesId || !speciesName) {
                  return [];
                }
                return `<li><a href="${getUserUrl(userNickname)}">@${userNickname}</a> reported a nice <a href="${getSpeciesUrl(
                  speciesId,
                  speciesName,
                )}">${speciesName}</a>${
                  fishingWaterId && fishingWaterName
                    ? ` at <a href="${getFishingWaterUrl(
                        fishingWaterId,
                        fishingWaterName,
                      )}">${fishingWaterName}</a>`
                    : ''
                }.</li>`;
              })
              .join('')}</ul>.`,
          },
        ]
      : []),
    {
      id: '4',
      title: `📅 What is the best time to go fishing in ${placeName}?`,
      body: (
        <>
          {`Download the `}
          <Button
            onClick={handleCTAClick}
            size="sm"
            className="body-text-sm text-primary font-semibold"
            variant="textNone"
          >
            Fishbrain app
          </Button>
          {`  and see today's Fishing and Weather Forecast for `}
          <PlaceLinks
            regionId={region?.id}
            regionName={region?.name}
            countryId={countryId}
            countryName={countryName}
            className="m-0 text-left"
            linkClassName="body-text-sm"
            textAs="span"
            textVariant="md"
            placeName={placeName}
            placeSlug={placeSlug}
          />
          {`. We give you weather trends and help you find the perfect window to catch your next personal best. With `}
          <InternalLink to={'/pro'} className="body-text-sm text-primary">
            Fishbrain Pro
          </InternalLink>
          {`, you will get the following capabilities: `}
          <ul className="m-0 list-disc px-4 py-2">
            <li className="body-text-sm m-0">
              <strong>Plan smarter:</strong> Get hour-by-hour fishing weather conditions.
            </li>
            <li className="body-text-sm m-0">
              <strong>Fish at peak times:</strong> Use <strong>BiteTime™</strong> predictions to
              maximize catches.
            </li>
            <li className="body-text-sm m-0">
              <strong>Stay ahead of the weather:</strong> Get hyper-local, real-time updates.
            </li>
          </ul>
        </>
      ),
      bodyString: `Download the Fishbrain app and see today's Fishing and Weather Forecast for ${getPlaceLinksString(
        {
          countryId,
          countryName,
          placeName,
          placeSlug,
          regionId: region?.id,
          regionName: region?.name,
        },
      )}. We give you weather trends and help you find the perfect window to catch your next personal best. With <a href="/pro">Fishbrain Pro</a>, you will get the following capabilities: <ul><li><strong>Plan smarter:</strong> Get hour-by-hour fishing weather conditions.</li><li><strong>Fish at peak times:</strong> Use <strong>BiteTime™</strong> predictions to maximize catches.</li><li><strong>Stay ahead of the weather:</strong> Get hyper-local, real-time updates.</li></ul>.`,
    },
  ];
};
